import { create } from 'zustand';
import { authenticationApi } from '@api/api-config';
import { UserDto } from '@api/generated/bff';
import { useCart } from '@hooks/useCart.ts';

interface AuthState {
  user: UserDto | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  forcePasswordChange: boolean;

  // Actions
  login: (login: string, password: string, rememberMe: boolean) => Promise<boolean>;
  logout: () => Promise<void>;
  forgotPassword: (login: string) => Promise<boolean>;
  forgotLogin: (email: string) => Promise<boolean>;
  resetPassword: (token: string, newPassword: string, confirmPassword: string) => Promise<boolean>;
  changePassword: (oldPassword: string, newPassword: string, confirmPassword: string) => Promise<boolean>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>(set => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  forcePasswordChange: false,

  login: async (login: string, password: string, rememberMe: boolean) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authenticationApi.login({
        loginRequest: {
          login,
          password,
          rememberMe,
        },
      });

      if (response.data.success && response.data.user) {
        const forcePasswordChange = response.data.forcePasswordChange === true;
        set({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          forcePasswordChange,
        });
        return true;
      } else {
        set({
          error: response.data.message || 'Přihlášení se nezdařilo',
          isLoading: false,
        });
        return false;
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nastala chyba při přihlašování. Zkuste to prosím znovu.';
      set({
        error: errorMessage,
        isLoading: false,
        isAuthenticated: false,
      });
      return false;
    }
  },

  logout: async () => {
    try {
      await authenticationApi.logout({});
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      set({
        user: null,
        isAuthenticated: false,
        error: null,
      });
      // Rozpracovana objednavka nesmi zustat v localStorage dalsimu, kdo se na tomhle pocitaci prihlasi.
      useCart.getState().clearOrderState();
      // Redirect to login
      window.location.href = '/login';
    }
  },

  forgotPassword: async (login: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authenticationApi.forgotPassword({
        forgotPasswordRequest: {
          login,
        },
      });

      set({ isLoading: false });
      return response.data.success || false;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nastala chyba při odesílání e-mailu. Zkuste to prosím znovu.';
      set({
        error: errorMessage,
        isLoading: false,
      });
      return false;
    }
  },

  forgotLogin: async (email: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authenticationApi.forgotLogin({
        forgotLoginRequest: {
          email,
        },
      });

      set({ isLoading: false });
      return response.data.success || false;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nastala chyba při odesílání e-mailu. Zkuste to prosím znovu.';
      set({
        error: errorMessage,
        isLoading: false,
      });
      return false;
    }
  },

  resetPassword: async (token: string, newPassword: string, confirmPassword: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authenticationApi.resetPassword({
        resetPasswordRequest: {
          token,
          newPassword,
          confirmPassword,
        },
      });

      if (response.data.success) {
        set({ isLoading: false, error: null });
        return true;
      } else {
        set({
          error: response.data.message || 'Obnovení hesla se nezdařilo',
          isLoading: false,
        });
        return false;
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nastala chyba při obnovení hesla. Zkuste to prosím znovu.';
      set({
        error: errorMessage,
        isLoading: false,
      });
      return false;
    }
  },

  changePassword: async (oldPassword: string, newPassword: string, confirmPassword: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authenticationApi.changePassword({
        changePasswordRequest: {
          oldPassword,
          newPassword,
          confirmPassword,
        },
      });

      if (response.data.success) {
        set({ isLoading: false, error: null, forcePasswordChange: false });
        return true;
      } else {
        set({
          error: response.data.message || 'Změna hesla se nezdařila',
          isLoading: false,
        });
        return false;
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nastala chyba při změně hesla. Zkuste to prosím znovu.';
      set({
        error: errorMessage,
        isLoading: false,
      });
      return false;
    }
  },

  checkAuth: async () => {
    set({ isLoading: true });

    try {
      const response = await authenticationApi.getCurrentUser();

      if (response.data) {
        set({
          user: response.data,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (err) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
